<template>
    <div class="container admin-table-page">
        <!-- Título y botón de retroceso -->
        <div class="param-title-wrap niif-header">
            <div class="d-flex align-items-center w-100">
                <div class="d-flex align-items-center">
                    <b-button class="page-back-btn mr-2" variant="link" @click="goBack">
                        <font-awesome-icon :icon="['fas', 'arrow-alt-circle-left']" class="back-icon" />
                    </b-button>
                    <h2 class="param-title">{{ tituloFijo }}</h2>
                </div>
            </div>
        </div>

        <!-- Filtros principales -->
        <div class="param-title-wrap niif-header">
            <div class="d-flex align-items-center w-100">
                <div class="filters-container d-flex align-items-center">
                    <div class="filter-date-wrapper">
                        <input 
                            type="month" 
                            v-model="selVigencia" 
                            @change="onValidityChange"
                            class="filter-date-input"
                            :class="{ 'disabled-period': isPeriodoDeshabilitado(selVigencia) }"
                            :disabled="isLoading || isLoadingCarga || isCalculatingCTA || showCTASuccessMessage"
                        />
                    </div>

                    <b-form-select
                        v-model="grupoId"
                        @change="f_filterCompany"
                        class="filter-select ml-2"
                        :disabled="isLoadingCarga || isCalculatingCTA || showCTASuccessMessage"
                    >
                        <b-form-select-option value="">{{ labels.selectGroup }}</b-form-select-option>
                        <option v-for="grupo in listGrupos" :key="grupo.id" :value="grupo.id">
                            {{ grupo?.nombre }}
                        </option>
                    </b-form-select>

                    <b-form-select
                        v-model="companyId"
                        @change="f_valide_formato"
                        class="filter-select ml-2"
                        :disabled="filteredCompany.length === 0 || isLoadingCarga || isCalculatingCTA || showCTASuccessMessage"
                    >
                        <b-form-select-option :value="''">{{ labels.selectCompany }}</b-form-select-option>
                        <option v-for="company in filteredCompany" :key="company.id" :value="String(company.id)">
                            {{ company?.acronym }} {{ company?.nombre }}
                        </option>
                    </b-form-select>

                    <!-- Botón CTA con estado de cálculo -->
                    <b-button
                        v-if="selVigencia && companyId && currencyCTA"
                        class="consult-btn ml-2"
                        @click="f_calcula_cta"
                        :disabled="isLoadingCarga || isCalculatingCTA || showSuccessMessage || showCTASuccessMessage"
                    >
                        <font-awesome-icon
                            :icon="['fas', 'calculator']"
                            class="mr-1"
                            :spin="isCalculatingCTA"
                        />
                        {{ isCalculatingCTA ? labels.calculatingCTA : labels.calculeCTA }}
                    </b-button>
                </div>
            </div>
        </div>

        <!-- Alerta de período deshabilitado -->
        <b-alert 
            v-if="selVigencia && isPeriodoDeshabilitado(selVigencia)" 
            show 
            variant="danger" 
            class="mt-3 text-center"
        >
            <font-awesome-icon :icon="['fas', 'exclamation-octagon']" class="mr-2" />
            <strong>PERÍODO DESHABILITADO:</strong> No se pueden cargar datos para el período
            {{ formatVigenciaMostrar(selVigencia) }} porque ha sido deshabilitado por el administrador.
            <br>
            <small>Si necesita cargar información para este período, contacte al administrador.</small>
        </b-alert>

        <!-- Tarjeta de carga -->
        <div class="admin-table-block mt-4">
            <div v-if="!isLoading">
                <div class="table-corporate-shell">
                    <div v-if="isUploading" class="upload-spinner-container">
                        <!-- Spinner moderno -->
                        <div class="modern-spinner">
                            <div class="spinner-ring"></div>
                            <div class="spinner-ring"></div>
                            <div class="spinner-ring"></div>
                        </div>
                        <p class="mt-3">{{ labels.loading }}</p>
                    </div>

                    <div v-else class="upload-content-wrapper">
                        <!-- Área de drop -->
                        <div
                            class="upload-drop-zone"
                            :class="{ 
                                'drag-over': isDragOver,
                                'has-file': fileName,
                                'disabled': !isFormularioCompleto || isLoadingCarga || isCalculatingCTA || showSuccessMessage || showCTASuccessMessage
                            }"
                            @dragover.prevent="isDragOver = true"
                            @dragleave.prevent="isDragOver = false"
                            @drop.prevent="handleDrop"
                            @click="triggerFileInput"
                        >
                            <input
                                ref="fileInput"
                                type="file"
                                accept=".xlsx,.xls"
                                class="hidden-file-input"
                                :disabled="isLoadingCarga || isCalculatingCTA || showSuccessMessage || showCTASuccessMessage"
                                @change="cargaExcel"
                            />

                            <div class="upload-content">
                                <font-awesome-icon
                                    :icon="['fas', 'cloud-upload-alt']"
                                    class="upload-icon"
                                    :class="{ 'has-file': fileName }"
                                />

                                <h5 class="upload-title" :class="{ 'text-muted': !isFormularioCompleto || isLoadingCarga || isCalculatingCTA || showSuccessMessage || showCTASuccessMessage }">
                                    {{ isLoadingCarga ? 'Procesando carga...' : (showSuccessMessage ? 'Carga completada' : (isCalculatingCTA ? 'Calculando CTA...' : (showCTASuccessMessage ? 'CTA Generado' : (fileName || labels.dropOrClick)))) }}
                                </h5>

                                <p v-if="!fileName && !isFormularioCompleto && !isLoadingCarga && !isCalculatingCTA && !showSuccessMessage && !showCTASuccessMessage" class="upload-subtitle text-muted">
                                    <small>{{ labels.completeFilters }}</small>
                                </p>

                                <p v-if="!fileName && isFormularioCompleto && !isLoadingCarga && !isCalculatingCTA && !showSuccessMessage && !showCTASuccessMessage" class="upload-subtitle">
                                    <small>{{ labels.supportedFormats }}</small>
                                </p>

                                <div v-if="fileName && !isLoadingCarga && !isCalculatingCTA && !showSuccessMessage && !showCTASuccessMessage" class="upload-file-info-wrapper">
                                    <font-awesome-icon :icon="['fas', 'file-excel']" class="text-success mr-2" />
                                    <span>{{ fileName }}</span>
                                    <b-button size="sm" variant="link" class="remove-file-btn ml-2" @click.stop="removeFile">
                                        <font-awesome-icon :icon="['fas', 'times']" />
                                    </b-button>
                                </div>
                            </div>
                        </div>

                        <!-- Selector de hoja - SIEMPRE VISIBLE -->
                        <div class="sheet-selector-container mt-3">
                            <b-form-select
                                v-model="selectedHoja"
                                @change="f_showHoja"
                                class="filter-select"
                                :disabled="!isFormularioCompleto || isLoadingCarga || isCalculatingCTA || showSuccessMessage || showCTASuccessMessage || listHojas.length === 0"
                            >
                                <b-form-select-option value="">{{ labels.selectSheet }}</b-form-select-option>
                                <option v-for="(item, idx) in listHojas" :key="idx" :value="item">{{ item }}</option>
                            </b-form-select>
                        </div>

                        <!-- Panel de confirmación - SIEMPRE VISIBLE (pero oculto durante carga o cálculo CTA) -->
                        <div v-if="!isLoadingCarga && !showSuccessMessage && !isCalculatingCTA && !showCTASuccessMessage" class="upload-action-panel mt-4">
                            <div class="confirmation-info-grid">
                                <div class="info-item">
                                    <span class="info-label">{{ labels.validity }}:</span>
                                    <span class="info-value" :class="{ 'text-muted': !selVigencia }">
                                        {{ selVigencia ? formatVigenciaMostrar(selVigencia) : 'No seleccionada' }}
                                    </span>
                                </div>
                                <div class="info-item">
                                    <span class="info-label">{{ labels.company }}:</span>
                                    <span class="info-value" :class="{ 'text-muted': !companyId }">
                                        {{ getCompanyName() || 'No seleccionada' }}
                                    </span>
                                </div>
                                <div class="info-item">
                                    <span class="info-label">{{ labels.file }}:</span>
                                    <span class="info-value" :class="{ 'text-muted': !fileName }">
                                        {{ getFileName() || 'No seleccionado' }}
                                    </span>
                                </div>
                                <div class="info-item">
                                    <span class="info-label">{{ labels.sheet }}:</span>
                                    <span class="info-value" :class="{ 'text-muted': !selectedHoja }">
                                        {{ selectedHoja || 'No seleccionada' }}
                                    </span>
                                </div>
                            </div>

                            <div class="alert alert-warning text-center mt-3" role="alert">
                                <font-awesome-icon :icon="['fas', 'exclamation-triangle']" class="mr-2" />
                                {{ labels.confirmWarning }}
                            </div>

                            <div class="text-center mt-3">
                                <b-button
                                    class="upload-btn"
                                    :disabled="!confirmButtonEnabled || isPeriodoDeshabilitado(selVigencia) || !selectedHoja"
                                    @click="confirmAndLoad()"
                                >
                                    <font-awesome-icon :icon="['fas', 'check']" class="mr-2" />
                                    {{ confirmButtonEnabled && selectedHoja ? labels.acceptAndLoad : `${labels.wait} ${countdown} ${labels.seconds}` }}
                                </b-button>
                            </div>
                        </div>

                        <!-- Spinner de carga moderno con barra de progreso -->
                        <div v-if="isLoadingCarga" class="upload-spinner-container mt-4">
                            <div class="modern-spinner">
                                <div class="spinner-ring"></div>
                                <div class="spinner-ring"></div>
                                <div class="spinner-ring"></div>
                            </div>
                            <p class="mt-3 font-weight-bold">{{ progressMessage }}</p>
                            <p class="text-muted small">{{ progressSubMessage }}</p>
                            <b-progress class="mt-2 modern-progress" style="max-width: 400px; width: 100%; height: 8px; border-radius: 4px;">
                                <b-progress-bar 
                                    :value="progressValue" 
                                    :label="`${progressValue}%`" 
                                    variant="primary" 
                                    striped 
                                    animated 
                                    class="rounded-pill"
                                />
                            </b-progress>
                        </div>

                        <!-- Spinner de cálculo CTA -->
                        <div v-if="isCalculatingCTA" class="upload-spinner-container mt-4">
                            <div class="modern-spinner">
                                <div class="spinner-ring"></div>
                                <div class="spinner-ring"></div>
                                <div class="spinner-ring"></div>
                            </div>
                            <p class="mt-3 font-weight-bold">{{ labels.ctaCalculatingTitle }}</p>
                            <p class="text-muted small">{{ labels.ctaCalculatingSub }}</p>
                        </div>

                        <!-- Mensaje de éxito de la carga -->
                        <div v-if="showSuccessMessage" class="upload-spinner-container mt-4">
                            <div class="success-message">
                                <font-awesome-icon :icon="['fas', 'check-circle']" class="success-icon" />
                                <p class="success-title">¡Carga completada exitosamente!</p>
                                <p class="success-subtitle">El archivo ha sido procesado correctamente</p>
                            </div>
                        </div>

                        <!-- Mensaje de éxito del CTA (persistente, luego se limpian los datos) -->
                        <div v-if="showCTASuccessMessage" class="upload-spinner-container mt-4">
                            <div class="success-message">
                                <font-awesome-icon :icon="['fas', 'check-circle']" class="success-icon" />
                                <p class="success-title">{{ labels.ctaSuccessTitle }}</p>
                                <p class="success-subtitle">{{ labels.ctaSuccessSubtitle }}</p>
                                <div class="cta-info mt-2">
                                    <font-awesome-icon :icon="['fas', 'calculator']" class="mr-2" />
                                    <span>{{ getCompanyName() }}</span>
                                    <span class="mx-1">—</span>
                                    <span>{{ formatVigenciaMostrar(selVigencia) }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="isLoading" class="text-center py-5">
                <div class="modern-spinner">
                    <div class="spinner-ring"></div>
                    <div class="spinner-ring"></div>
                    <div class="spinner-ring"></div>
                </div>
                <p class="mt-3">{{ labels.loading }}</p>
            </div>
        </div>
    </div>
</template>

<script>
import Company from "../../services/Company";
import Cargue from "../../services/Cargue";
import * as XLSX from "xlsx";
import Grupo from "../../services/Grupo";

export default {
    name: "CargueBalance",
    data: () => ({
        listGrupos: [],
        grupoId: '',
        filteredCompany: [],
        listCompany: [],
        companyId: '',
        selVigencia: "",
        formato_asignado: false,
        listCuenta: [],
        listHojas: [],
        showBtnHoja: false,
        isLoading: false,
        isUploading: false,
        isLoadingCarga: false,
        showSuccessMessage: false,
        isCalculatingCTA: false,
        showCTASuccessMessage: false,
        workbook: "",
        selectedHoja: '',
        fileReader: '',
        formato: null,
        currencyCTA: false,
        confirmButtonEnabled: false,
        countdown: 5,
        countdownInterval: null,
        progressValue: 0,
        progressMessage: 'Iniciando carga...',
        progressSubMessage: 'Procesando archivo Excel',
        fileName: '',
        isDragOver: false,
        successTimer: null,
        ctaSuccessTimer: null,
    }),
    computed: {
        isFormularioCompleto() {
            return !!(this.selVigencia && this.companyId && this.formato_asignado && !this.isPeriodoDeshabilitado(this.selVigencia));
        },
        tituloFijo() {
            return "Proceso De Cargue De Balances";
        },
        labels() {
            const isSpanish = this.$store.state._language === "es-ES";
            return {
                selectValidity: isSpanish ? "Seleccione Vigencia" : "Select Validity",
                selectGroup: isSpanish ? "Seleccione Grupo" : "Select Group",
                selectCompany: isSpanish ? "Seleccione Compañía" : "Select Company",
                selectSheet: isSpanish ? "Seleccione Hoja" : "Select Sheet",
                calculeCTA: isSpanish ? "Currency Translate Adjustment" : "Currency Translate Adjustment",
                calculatingCTA: isSpanish ? "Calculando..." : "Calculating...",
                ctaCalculatingTitle: isSpanish ? "Calculando CTA..." : "Calculating CTA...",
                ctaCalculatingSub: isSpanish ? "Procesando el Currency Translate Adjustment" : "Processing the Currency Translate Adjustment",
                ctaSuccessTitle: isSpanish ? "¡CTA Generado exitosamente!" : "CTA generated successfully!",
                ctaSuccessSubtitle: isSpanish ? "El Currency Translate Adjustment ha sido calculado correctamente" : "The Currency Translate Adjustment has been calculated successfully",
                dropOrClick: isSpanish ? "Arrastre o haga clic para seleccionar un archivo Excel" : "Drag or click to select an Excel file",
                supportedFormats: isSpanish ? "Formatos soportados: .xlsx, .xls" : "Supported formats: .xlsx, .xls",
                completeFilters: isSpanish ? "Complete los filtros para habilitar la carga" : "Complete filters to enable upload",
                validity: isSpanish ? "Vigencia" : "Validity",
                company: isSpanish ? "Compañía" : "Company",
                file: isSpanish ? "Archivo" : "File",
                sheet: isSpanish ? "Hoja" : "Sheet",
                confirmWarning: isSpanish ? "Por favor verifique que la fecha sea correcta. Esta acción no se puede deshacer." : "Please verify that the date is correct. This action cannot be undone.",
                acceptAndLoad: isSpanish ? "Aceptar y Cargar" : "Accept and Load",
                wait: isSpanish ? "Esperar" : "Wait",
                seconds: isSpanish ? "segundos" : "seconds",
                loading: isSpanish ? "Cargando..." : "Loading...",
            };
        }
    },
    watch: {
        selVigencia(newVal) {
            if (newVal && this.isPeriodoDeshabilitado(newVal)) {
                this.companyId = '';
                this.formato_asignado = false;
                this.currencyCTA = false;
                this.showBtnHoja = false;
                this.selectedHoja = '';
                this.listHojas = [];
                this.fileName = '';
                this.formato = null;
                if (this.$bvToast) {
                    this.$bvToast.toast(
                        `El período ${this.formatVigenciaMostrar(newVal)} está deshabilitado. No se permiten cargas.`,
                        { title: 'Período Bloqueado', variant: 'warning', solid: true, autoHideDelay: 4000 }
                    );
                }
            }
        },
        // Forzar validación cuando cambia companyId
        companyId(newVal) {
            if (newVal) {
                this.$nextTick(() => {
                    this.f_valide_formato();
                });
            }
        }
    },
    methods: {
        goBack() { this.$router.back(); },

        formatVigenciaMostrar(vigencia) {
            if (!vigencia) return 'No seleccionada';
            let year, month;
            if (vigencia.includes('-')) {
                const partes = vigencia.split('-');
                year = partes[0];
                month = parseInt(partes[1]);
            } else {
                year = vigencia.substring(0, 4);
                month = parseInt(vigencia.substring(4, 6));
            }
            if (isNaN(month) || month < 1 || month > 12) return vigencia;
            const meses = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
                'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
            return `${meses[month - 1]} ${year}`;
        },

        formatVigenciaBackend(vigencia) {
            if (!vigencia) return '';
            return vigencia.replace('-', '');
        },

        isPeriodoDeshabilitado(periodo) {
            if (!periodo) return false;
            return !this.$store.getters.isPeriodoEnabled(this.formatVigenciaBackend(periodo));
        },

        onValidityChange() {
            if (this.selVigencia && this.isPeriodoDeshabilitado(this.selVigencia)) {
                this.$bvToast?.toast(
                    `No se puede trabajar con el período ${this.formatVigenciaMostrar(this.selVigencia)} porque está deshabilitado.`,
                    { title: 'Período Deshabilitado', variant: 'danger', solid: true, autoHideDelay: 5000 }
                );
                this.companyId = '';
                this.formato_asignado = false;
                this.currencyCTA = false;
                this.listHojas = [];
                this.fileName = '';
                this.formato = null;
                this.resetForm();
                return;
            }
            this.companyId = '';
            this.formato_asignado = false;
            this.selectedHoja = '';
            this.fileName = '';
            this.listHojas = [];
            this.showBtnHoja = false;
            this.formato = null;
            this.resetCountdown();
        },

        f_filterCompany(grupoSeleccionado) {
            this.grupoId = grupoSeleccionado;
            this.filteredCompany = !grupoSeleccionado ? [...this.listCompany] : 
                this.listCompany.filter(company => {
                    if (!company?.id_grupos) return false;
                    return String(company.id_grupos).split(',').map(id => id.trim()).includes(String(grupoSeleccionado).trim());
                });
            this.companyId = "";
            this.formato_asignado = false;
            this.currencyCTA = false;
            this.selectedHoja = "";
            this.fileName = "";
            this.listHojas = [];
            this.showBtnHoja = false;
            this.resetCountdown();
        },

        f_valide_formato() {
            if (!this.companyId) return;
            
            if (!this.f_validatePeriodo()) return;
            
            this.currencyCTA = false;
            this.formato_asignado = false;
            this.selectedHoja = '';
            this.fileName = '';
            this.listHojas = [];
            this.showBtnHoja = false;
            this.formato = null;

            const objFormato = this.listCompany.find(obj => Number(obj.id) === Number(this.companyId));
            if (objFormato?.formato) {
                this.formato = objFormato.formato;
                this.formato_asignado = true;
                if (objFormato.id_moneda !== 2) this.currencyCTA = true;
                // Forzar reactividad
                this.$forceUpdate();
            } else {
                this.$bvToast?.toast(objFormato?.nombre + " sin formato asignado", { title: 'Validación', variant: 'warning', solid: true });
                this.formato = null;
            }
        },

        f_validatePeriodo() {
            if (this.selVigencia && this.isPeriodoDeshabilitado(this.selVigencia)) {
                this.$bvToast?.toast(
                    `No se puede trabajar con el período ${this.formatVigenciaMostrar(this.selVigencia)} porque está deshabilitado.`,
                    { title: 'Período Deshabilitado', variant: 'danger', solid: true, autoHideDelay: 5000 }
                );
                return false;
            }
            return true;
        },

        triggerFileInput() {
            if (this.isLoadingCarga || this.isCalculatingCTA || this.showSuccessMessage || this.showCTASuccessMessage) return;
            if (!this.isFormularioCompleto) {
                this.$bvToast?.toast('Complete todos los filtros para habilitar la carga.', { title: 'Validación', variant: 'warning', solid: true });
                return;
            }
            this.$refs.fileInput?.click();
        },

        handleDrop(event) {
            if (this.isLoadingCarga || this.isCalculatingCTA || this.showSuccessMessage || this.showCTASuccessMessage) return;
            if (!this.isFormularioCompleto) {
                this.$bvToast?.toast('Complete todos los filtros para habilitar la carga.', { title: 'Validación', variant: 'warning', solid: true });
                return;
            }
            this.isDragOver = false;
            const files = event.dataTransfer.files;
            if (files.length > 0) this.processFile(files[0]);
        },

        processFile(file) {
            const validTypes = ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel'];
            if (!validTypes.includes(file.type) && !file.name.match(/\.(xlsx|xls)$/)) {
                this.$bvToast?.toast('Por favor seleccione un archivo Excel válido (.xlsx o .xls)', { title: 'Error', variant: 'danger', solid: true });
                return;
            }
            
            this.isUploading = true;
            this.fileName = file.name;
            this.fileReader = new FileReader();
            this.fileReader.onload = (event) => {
                try {
                    const data = event.target.result;
                    this.workbook = XLSX.read(data, { type: "binary" });
                    this.listHojas = this.workbook.SheetNames;
                    this.selectedHoja = '';
                    this.showBtnHoja = false;
                    this.isUploading = false;
                    this.resetCountdown();
                    this.$bvToast?.toast(`Archivo "${file.name}" cargado correctamente`, { title: 'Éxito', variant: 'success', solid: true });
                } catch (error) {
                    this.isUploading = false;
                    this.$bvToast?.toast('Error al leer el archivo Excel', { title: 'Error', variant: 'danger', solid: true });
                    console.error(error);
                }
            };
            this.fileReader.readAsBinaryString(file);
        },

        cargaExcel(event) {
            const file = event.target.files[0];
            if (file) this.processFile(file);
            event.target.value = '';
        },

        removeFile() {
            if (this.isLoadingCarga || this.isCalculatingCTA || this.showSuccessMessage || this.showCTASuccessMessage) return;
            this.fileName = '';
            this.selectedHoja = '';
            this.listHojas = [];
            this.showBtnHoja = false;
            this.workbook = null;
            this.fileReader = null;
            this.isUploading = false;
            if (this.$refs.fileInput) this.$refs.fileInput.value = '';
            this.resetCountdown();
        },

        f_showHoja() {
            if (this.isLoadingCarga || this.isCalculatingCTA || this.showSuccessMessage || this.showCTASuccessMessage) return;
            this.showBtnHoja = true;
            this.startCountdown();
        },

        async f_calcula_cta() {
            // Evitar doble ejecución o ejecución durante una carga / otro proceso
            if (this.isLoadingCarga || this.isCalculatingCTA || this.showSuccessMessage || this.showCTASuccessMessage) return;

            if (!this.selVigencia || !this.companyId) {
                this.$bvToast?.toast(
                    'Seleccione vigencia y compañía para calcular el CTA.',
                    { title: 'Validación', variant: 'warning', solid: true, autoHideDelay: 5000, toaster: 'b-toaster-top-center' }
                );
                return;
            }

            const vigencia = this.formatVigenciaBackend(this.selVigencia);
            const company = this.companyId;
            const userId = this.$store.state.userId;

            console.log('=== CLICK CURRENCY ===');
            console.log('Llamando calculaCTA con:', { vigencia, company, userId });

            this.isCalculatingCTA = true;

            try {
                const res = await Cargue.calculaCTA(vigencia, company, userId);
                console.log('Respuesta calculaCTA:', res);

                if (res.status === 200) {
                    // Feedback persistente en pantalla (mismo patrón que la carga de balance)
                    this.showCTASuccessMessage = true;

                    this.$bvToast?.toast('CTA Generado correctamente', {
                        title: '✅ Éxito',
                        variant: 'success',
                        solid: true,
                        autoHideDelay: 5000,
                        toaster: 'b-toaster-top-center'
                    });

                    // Limpiar los datos después de mostrar el mensaje (igual que al cargar un balance)
                    if (this.ctaSuccessTimer) clearTimeout(this.ctaSuccessTimer);
                    this.ctaSuccessTimer = setTimeout(() => {
                        this.showCTASuccessMessage = false;
                        this.resetForm();
                        this.ctaSuccessTimer = null;
                    }, 3000);
                } else {
                    this.$bvToast?.toast('Problema al generar CTA', {
                        title: '⚠️ Advertencia',
                        variant: 'warning',
                        solid: true,
                        autoHideDelay: 5000,
                        toaster: 'b-toaster-top-center'
                    });
                }
            } catch (err) {
                console.error('Error en calculaCTA:', err);
                console.error('Response:', err?.response);
                this.$bvToast?.toast(
                    'Error al generar CTA: ' + (err?.response?.data?.message || err?.message || 'desconocido'),
                    { title: '❌ Error', variant: 'danger', solid: true, autoHideDelay: 5000, toaster: 'b-toaster-top-center' }
                );
            } finally {
                this.isCalculatingCTA = false;
            }
        },

        resetCountdown() {
            if (this.countdownInterval) { clearInterval(this.countdownInterval); this.countdownInterval = null; }
            this.confirmButtonEnabled = false;
            this.countdown = 5;
        },

        startCountdown() {
            this.resetCountdown();
            this.countdownInterval = setInterval(() => {
                this.countdown -= 1;
                if (this.countdown <= 0) {
                    this.confirmButtonEnabled = true;
                    clearInterval(this.countdownInterval);
                    this.countdownInterval = null;
                }
            }, 1000);
        },

        confirmAndLoad() {
            if (this.isPeriodoDeshabilitado(this.selVigencia)) {
                this.$bvToast?.toast('No se puede proceder con la carga: período deshabilitado', { title: 'Error', variant: 'danger', solid: true });
                return;
            }
            if (this.countdownInterval) { clearInterval(this.countdownInterval); this.countdownInterval = null; }
            this.cargaBalanceSeleccionado();
        },

        getCompanyName() {
            if (!this.companyId) return '';
            const company = this.listCompany.find(c => Number(c.id) === Number(this.companyId));
            return company ? `${company?.acronym} ${company?.nombre}` : '';
        },

        getFileName() { return this.fileName || ''; },

        cargaBalanceSeleccionado() {
            this.isLoadingCarga = true;
            this.showSuccessMessage = false;
            this.progressValue = 0;
            this.progressMessage = 'Cargando archivo...';
            this.progressSubMessage = 'Procesando datos del Excel';

            const progressInterval = setInterval(() => {
                if (this.progressValue < 90) {
                    this.progressValue += 10;
                    if (this.progressValue === 30) this.progressSubMessage = 'Validando información';
                    else if (this.progressValue === 60) this.progressSubMessage = 'Guardando en base de datos';
                }
            }, 500);

            const rowObject = XLSX.utils.sheet_to_row_object_array(this.workbook.Sheets[this.selectedHoja]);
            this.listCuenta = rowObject.map(fila => Object.values(fila));

            const form = {
                vigencia: this.formatVigenciaBackend(this.selVigencia),
                company: this.companyId,
                listadoCuentas: this.listCuenta,
                idUser: this.$store.state.userId
            };

            const handleResponse = (res) => {
                clearInterval(progressInterval);
                if (res.status !== 200) {
                    this.progressMessage = 'Error en la carga';
                    this.progressSubMessage = 'Por favor intente nuevamente';
                    this.$bvToast?.toast("Problema al Cargar", { title: 'Error', variant: 'danger', solid: true });
                    setTimeout(() => { this.isLoadingCarga = false; this.resetForm(); }, 2000);
                } else {
                    this.actualizaNiif(form.vigencia, form.company);
                    this.progressValue = 100;
                    this.progressMessage = '¡Carga completada!';
                    this.progressSubMessage = 'Actualizando información NIIF';
                    
                    setTimeout(() => {
                        this.isLoadingCarga = false;
                        this.showSuccessMessage = true;
                        this.showBtnHoja = false;
                        if (this.successTimer) clearTimeout(this.successTimer);
                        this.successTimer = setTimeout(() => {
                            this.showSuccessMessage = false;
                            this.resetForm();
                            this.successTimer = null;
                        }, 3000);
                    }, 2000);
                }
            };

            const cargaMethods = {
                1: Cargue.cargaBalace,   2: Cargue.cargaBalaceF2,   3: Cargue.cargaBalaceF3,
                4: Cargue.cargaBalaceF4, 5: Cargue.cargaBalaceF5,   6: Cargue.cargaBalaceF6,
                7: Cargue.cargaBalaceF7, 8: Cargue.cargaBalaceF8,   9: Cargue.cargaBalaceF9,
                10: Cargue.cargaBalaceF10, 100: Cargue.cargaBalaceF100, 200: Cargue.cargaBalaceF200,
                300: Cargue.cargaBalaceF300, 400: Cargue.cargaBalaceF400, 500: Cargue.cargaBalaceF500,
                600: Cargue.cargaBalaceF600, 700: Cargue.cargaBalaceF700, 800: Cargue.cargaBalaceF800,
                900: Cargue.cargaBalaceF900,
            };

            const method = cargaMethods[this.formato];
            if (method) {
                method(form).then(handleResponse).catch(() => {
                    clearInterval(progressInterval);
                    this.$bvToast?.toast("Error al cargar el archivo", { title: 'Error', variant: 'danger', solid: true });
                    this.isLoadingCarga = false;
                    this.resetForm();
                });
            } else {
                clearInterval(progressInterval);
                this.$bvToast?.toast("Formato no soportado", { title: 'Error', variant: 'danger', solid: true });
                this.isLoadingCarga = false;
                this.resetForm();
            }
        },

        actualizaNiif(p_vigencia, p_compania) {
            Cargue.actualizaNiif(p_vigencia, p_compania)
                .then(res => {
                    this.$bvToast?.toast(res.status === 200 ? "Cargue Completo" : "Error(1) al Actualizar Niif",
                        { title: res.status === 200 ? 'Éxito' : 'Error', variant: res.status === 200 ? 'success' : 'danger', solid: true });
                })
                .catch(() => this.$bvToast?.toast("Error(2) al Actualizar Niif", { title: 'Error', variant: 'danger', solid: true }));
        },

        resetForm() {
            if (this.successTimer) { clearTimeout(this.successTimer); this.successTimer = null; }
            if (this.ctaSuccessTimer) { clearTimeout(this.ctaSuccessTimer); this.ctaSuccessTimer = null; }
            this.grupoId = '';
            this.companyId = '';
            this.formato_asignado = false;
            this.listHojas = [];
            this.showBtnHoja = false;
            this.selectedHoja = '';
            this.formato = null;
            this.fileName = '';
            this.currencyCTA = false;
            this.workbook = null;
            this.fileReader = null;
            this.isUploading = false;
            this.isLoadingCarga = false;
            this.showSuccessMessage = false;
            this.isCalculatingCTA = false;
            this.showCTASuccessMessage = false;
            this.progressValue = 0;
            this.progressMessage = 'Iniciando carga...';
            this.progressSubMessage = 'Procesando archivo Excel';
            if (this.$refs.fileInput) this.$refs.fileInput.value = '';
            this.filteredCompany = this.listCompany;
            this.resetCountdown();
            this.$forceUpdate();
        }
    },
    mounted() {
        this.isLoading = true;
        Promise.all([
            Grupo.getAllGrupos(),
            Company?.getAllCompanies(),
            Cargue?.getVigenciaCargue()
        ])
        .then(([gruposRes, companiesRes]) => {
            this.listGrupos = gruposRes.data.grupos || [];
            this.listCompany = companiesRes.data.companies || [];
            this.filteredCompany = this.listCompany;
        })
        .catch(err => {
            console.error(err);
            this.$bvToast?.toast('Error al cargar datos iniciales', { title: 'Error', variant: 'danger', solid: true });
        })
        .finally(() => this.isLoading = false);
    },
    beforeDestroy() {
        if (this.countdownInterval) { clearInterval(this.countdownInterval); this.countdownInterval = null; }
        if (this.successTimer) { clearTimeout(this.successTimer); this.successTimer = null; }
        if (this.ctaSuccessTimer) { clearTimeout(this.ctaSuccessTimer); this.ctaSuccessTimer = null; }
    }
};
</script>

<style src="../main.css"></style>

<style scoped>
/* ===== SPINNER MODERNO ===== */
.modern-spinner {
    position: relative;
    width: 60px;
    height: 60px;
    margin: 0 auto;
}

.spinner-ring {
    position: absolute;
    width: 100%;
    height: 100%;
    border-radius: 50%;
    border: 4px solid transparent;
    animation: spin 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite;
}

.spinner-ring:nth-child(1) {
    border-top-color: #1f4f83;
    animation-delay: -0.45s;
}

.spinner-ring:nth-child(2) {
    border-right-color: #4a90d9;
    animation-delay: -0.3s;
}

.spinner-ring:nth-child(3) {
    border-bottom-color: #7fb3e6;
    animation-delay: -0.15s;
}

@keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}

/* Variante más pequeña para carga inicial */
.modern-spinner.small {
    width: 40px;
    height: 40px;
}

.modern-spinner.small .spinner-ring {
    border-width: 3px;
}

/* Barra de progreso moderna */
.modern-progress {
    background: #e9ecef;
    overflow: hidden;
    box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.1);
}

.modern-progress .progress-bar {
    background: linear-gradient(90deg, #1f4f83, #4a90d9);
    transition: width 0.3s ease;
    font-weight: 600;
    font-size: 0.75rem;
    line-height: 8px;
    color: transparent;
    text-shadow: none;
}

/* ===== FIN SPINNER MODERNO ===== */

.niif-header .filters-container {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
    margin-left: 0 !important;
    flex-wrap: wrap;
}

.filter-date-wrapper {
    display: inline-flex;
    align-items: center;
}

.filter-date-input {
    min-width: 180px;
    max-width: 260px;
    width: auto;
    height: 38px;
    font-size: 14px;
    border-radius: 7px;
    border: 1px solid #ced4da;
    padding: 0 12px;
    background-color: #fff;
    color: #495057;
    transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.filter-date-input:focus {
    border-color: #80bdff;
    outline: 0;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.filter-date-input.disabled-period {
    border-color: #dc3545;
    background-color: #f8d7da;
}

.filter-date-input:disabled {
    background-color: #e9ecef;
    opacity: 1;
    cursor: not-allowed;
}

.niif-header .filter-select {
    min-width: 180px;
    max-width: 260px;
    width: auto;
    height: 38px;
    font-size: 14px;
    border-radius: 7px;
}

.niif-header .filter-select:disabled {
    opacity: 0.65;
    cursor: not-allowed;
    background-color: #e9ecef;
}

.niif-header .consult-btn {
    height: 38px;
    min-height: 38px;
    padding: 0 20px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
    border-radius: 7px;
    font-size: 14px;
    background-color: #6c757d !important;
    color: white !important;
    border: 1px solid #6c757d !important;
    cursor: pointer;
    flex-shrink: 0;
}

.niif-header .consult-btn:hover:not(:disabled) {
    background-color: #5a6268 !important;
    border-color: #545b62 !important;
}

.niif-header .consult-btn:disabled {
    opacity: 0.65;
    cursor: not-allowed;
}

.page-back-btn {
    padding: 0;
    font-size: 1.5rem;
    line-height: 1;
}

.back-icon {
    font-size: 1.75rem !important;
    width: 1.75rem !important;
    height: 1.75rem !important;
}

.param-title {
    font-size: 2rem !important;
    font-weight: 500;
}

.admin-table-block {
    margin-top: 1.5rem !important;
}

.table-corporate-shell {
    background: #ffffff;
    border: 1px solid #e4e8ed;
    border-radius: 10px;
    padding: 2rem;
    box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075) !important;
    min-height: 400px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    width: 100%;
    position: relative;
}

.upload-spinner-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 2rem 0;
    width: 100%;
    min-height: 200px;
}

.upload-content-wrapper {
    width: 100%;
    max-width: 100%;
}

.hidden-file-input {
    display: none;
}

.upload-drop-zone {
    width: 100%;
    border: 2px dashed #d1d5db;
    border-radius: 12px;
    padding: 3rem 2rem;
    cursor: pointer;
    background-color: #fafbfc;
    position: relative;
    min-height: 200px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
}

.upload-drop-zone.disabled {
    opacity: 0.6;
    cursor: not-allowed;
    border-color: #d1d5db;
    background-color: #f8f9fa;
}

.upload-drop-zone.has-file {
    border-color: #2e507a;
    background-color: #e8edf5;
    padding: 1.5rem 2rem;
    min-height: 120px;
}

.upload-drop-zone.drag-over {
    border-color: #2e507a;
    background-color: #dce3ef;
    transform: scale(1.01);
}

.upload-content {
    width: 100%;
    text-align: center;
}

.upload-icon {
    font-size: 3.5rem;
    color: #6c757d;
    margin-bottom: 1rem;
    transition: all 0.3s ease;
}

.upload-icon.has-file {
    font-size: 2.5rem;
    color: #2e507a;
    margin-bottom: 0.5rem;
}

.upload-title {
    color: #495057;
    font-weight: 500;
    margin-bottom: 0.5rem;
    font-size: 1.1rem;
    word-break: break-all;
}

.upload-title.text-muted {
    color: #6c757d !important;
}

.upload-subtitle {
    color: #6c757d;
    margin-bottom: 0;
    font-size: 0.9rem;
}

.upload-subtitle.text-muted {
    color: #adb5bd !important;
}

.upload-file-info-wrapper {
    display: inline-flex;
    align-items: center;
    padding: 0.5rem 1rem;
    background: #ffffff;
    border-radius: 6px;
    font-weight: 500;
    margin-top: 0.5rem;
    font-size: 0.95rem;
    border: 1px solid #e9ecef;
    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
}

.remove-file-btn {
    padding: 0 0.3rem;
    color: #dc3545;
    font-size: 1.1rem;
    cursor: pointer;
    line-height: 1;
}

.sheet-selector-container {
    max-width: 400px;
    margin: 1.5rem auto 0;
}

.upload-action-panel {
    width: 100%;
    max-width: 800px;
    margin: 0 auto;
    padding: 1.5rem;
    background: #f8fafc;
    border-radius: 10px;
    border: 1px solid #e4e8ed;
}

.confirmation-info-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.75rem 2rem;
}

.info-item {
    display: flex;
    justify-content: space-between;
    padding: 0.4rem 0;
    border-bottom: 1px solid #e9ecef;
}

.info-label {
    font-weight: 600;
    color: #495057;
}

.info-value {
    color: #2e507a;
    font-weight: 500;
}

.info-value.text-muted {
    color: #6c757d !important;
    font-weight: 400;
}

.upload-btn {
    min-width: 200px;
    padding: 0.6rem 2rem;
    font-size: 1rem;
    border-radius: 7px;
    background-color: #1f4f83 !important;
    border-color: #1f4f83 !important;
    color: white !important;
}

.upload-btn:hover:not(:disabled) {
    background-color: #1a446f !important;
    border-color: #1a446f !important;
}

.upload-btn:disabled {
    opacity: 0.65;
    cursor: not-allowed;
    background-color: #6c757d !important;
    border-color: #6c757d !important;
}

.success-message {
    text-align: center;
    padding: 1.5rem 0;
    animation: fadeInUp 0.5s ease;
}

.success-icon {
    font-size: 4rem;
    color: #28a745;
    margin-bottom: 1rem;
    display: block;
}

.success-title {
    font-size: 1.3rem;
    font-weight: 600;
    color: #28a745;
    margin-bottom: 0.5rem;
}

.success-subtitle {
    font-size: 1rem;
    color: #6c757d;
}

/* ===== INFORMACIÓN DEL CTA GENERADO ===== */
.cta-info {
    display: inline-flex;
    align-items: center;
    padding: 0.45rem 1rem;
    background: #e8f5e9;
    border: 1px solid #c3e6cb;
    border-radius: 6px;
    color: #155724;
    font-weight: 500;
    font-size: 0.9rem;
    margin-top: 0.5rem;
}

@keyframes fadeInUp {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
}

@media (max-width: 992px) {
    .confirmation-info-grid { grid-template-columns: 1fr; gap: 0.5rem; }
}

@media (max-width: 768px) {
    .upload-drop-zone { padding: 1.5rem 1rem; min-height: 140px; }
    .upload-title { font-size: 0.95rem; }
    .upload-btn { min-width: 100%; width: 100%; }
    .sheet-selector-container { max-width: 100%; }
    .table-corporate-shell { padding: 1rem; min-height: 250px; }
    .upload-icon { font-size: 2.5rem; }
    .upload-drop-zone.has-file { padding: 1rem; min-height: 100px; }
    .upload-action-panel { padding: 1rem; }
    .success-icon { font-size: 3rem; }
    .success-title { font-size: 1.1rem; }
    .modern-spinner { width: 50px; height: 50px; }
}

@media (max-width: 576px) {
    .param-title { font-size: 1.5rem !important; }
    .upload-drop-zone { padding: 1rem 0.75rem; min-height: 120px; }
    .upload-icon { font-size: 2rem; }
    .table-corporate-shell { padding: 0.75rem; min-height: 200px; }
    .upload-file-info-wrapper { font-size: 0.8rem; padding: 0.3rem 0.6rem; }
    .upload-drop-zone.has-file { min-height: 80px; padding: 0.75rem; }
    .success-icon { font-size: 2.5rem; }
    .success-title { font-size: 1rem; }
    .cta-info { font-size: 0.8rem; }
    .modern-spinner { width: 40px; height: 40px; }
    .modern-spinner .spinner-ring { border-width: 3px; }
}
</style>